import { FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@linktivity/link-ui';
import { Back } from '@triplabo/web/components';
import Checked from '@triplabo/web/assets/checked.svg?react';
import styles from './cancel.module.css';

interface CancelReasonProps {
  onBack: () => void;
  onConfirm: (reason: string) => void;
  selectedReason: string;
  setSelectedReason: (reason: string) => void;
}

const CancelReason: FC<CancelReasonProps> = ({
  onBack,
  onConfirm,
  selectedReason,
  setSelectedReason
}) => {
  const { t } = useTranslation();
  const [customReason, setCustomReason] = useState('');
  const otherReasonLabel = t('views.cancelOrder.selectReason.otherReason');

  const reasons = [
    {
      title: t('views.cancelOrder.selectReason.reasons.platform.title'),
      items: [
        t('views.cancelOrder.selectReason.reasons.platform.content.tooFar'),
        t(
          'views.cancelOrder.selectReason.reasons.platform.content.inaccurateLocation'
        ),
        t(
          'views.cancelOrder.selectReason.reasons.platform.content.notExpectedCar'
        )
      ]
    },
    {
      title: t('views.cancelOrder.selectReason.reasons.driver.title'),
      items: [
        t(
          'views.cancelOrder.selectReason.reasons.driver.content.driverNotComing'
        ),
        t(
          'views.cancelOrder.selectReason.reasons.driver.content.driverNotContactable'
        ),
        t('views.cancelOrder.selectReason.reasons.driver.content.driverCancel'),
        t('views.cancelOrder.selectReason.reasons.driver.content.driverLate'),
        t(
          'views.cancelOrder.selectReason.reasons.driver.content.driverNotMatch'
        )
      ]
    },
    {
      title: t('views.cancelOrder.selectReason.reasons.self.title'),
      items: [
        t('views.cancelOrder.selectReason.reasons.self.content.selfChange'),
        t('views.cancelOrder.selectReason.reasons.self.content.otherPlatform'),
        t('views.cancelOrder.selectReason.reasons.self.content.cannotArrive')
      ]
    }
  ];

  const handleReasonSelect = useCallback(
    (text: string) => {
      setSelectedReason(text);
      if (text !== otherReasonLabel) {
        setCustomReason('');
      }
    },
    [setSelectedReason, otherReasonLabel]
  );

  const handleCustomReasonChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const value = e.target.value;
      setCustomReason(value.trim());
    },
    []
  );

  const handleSubmit = useCallback(() => {
    if (selectedReason === otherReasonLabel) {
      if (customReason) {
        onConfirm(customReason);
      }
    } else {
      onConfirm(selectedReason);
    }
  }, [selectedReason, otherReasonLabel, onConfirm, customReason]);

  return (
    <div className={styles.wrap}>
      <Back
        onBack={onBack}
        inline
        title={t('views.cancelOrder.selectReason.title')}
      />
      <div className={styles.reasonList}>
        {reasons.map(group => (
          <div key={group.title} className={styles.reasonGroup}>
            <div className={styles.reasonGroupTitle}>{group.title}</div>
            {group.items.map(text => (
              <div
                key={text}
                className={styles.reasonItem}
                onClick={() => handleReasonSelect(text)}
              >
                <span>{text}</span>
                {selectedReason === text ? (
                  <Checked className={styles.checked} />
                ) : (
                  <div className={styles.unChecked} />
                )}
              </div>
            ))}
          </div>
        ))}

        <div className={styles.reasonGroup}>
          <div className={styles.reasonGroupTitle}>
            {t('views.cancelOrder.selectReason.otherReason')}
          </div>
          <div
            className={styles.reasonItem}
            onClick={() => handleReasonSelect(otherReasonLabel)}
          >
            <span>{otherReasonLabel}</span>
            {selectedReason === otherReasonLabel ? (
              <Checked className={styles.checked} />
            ) : (
              <div className={styles.unChecked} />
            )}
          </div>
          <div
            className={styles.customReasonContainer}
            onClick={e => e.stopPropagation()}
          >
            <textarea
              className={styles.customReasonInput}
              placeholder={t(
                'views.cancelOrder.selectReason.customReasonPlaceholder'
              )}
              value={customReason}
              onChange={handleCustomReasonChange}
              maxLength={100}
              autoFocus={selectedReason === otherReasonLabel}
            />
            <div className={styles.customReasonCount}>
              {customReason.length}/100
            </div>
          </div>
        </div>
      </div>

      <div className={styles.submitBar}>
        <Button
          className={styles.submitBtn}
          onClick={handleSubmit}
          disabled={
            !selectedReason ||
            (selectedReason === otherReasonLabel && !customReason)
          }
        >
          {t('views.cancelOrder.selectReason.submit')}
        </Button>
      </div>
    </div>
  );
};

export default CancelReason;
